// app/javascript/projects/components/ProjectsDisplay.jsx

import {
  Switch,
  Route,
} from 'react-router-dom'
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from 'ui'
import LogAPI from 'apis/LogAPI';
import { MemoModalProvider } from './modal/MemoModalProvider';
import { useTranscriptWithErrorHandling } from 'hooks/transcripts/useTranscriptWithErrorHandling';
import { useTranscript } from 'hooks/transcripts/queries';
import useNumberOfPages from 'hooks/useNumberOfPages';
import { useHistory, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { codeKeys } from 'hooks/codes/queries';
import {
  TRANSCRIPT_NAVIGATION_PADDING
} from 'constants/DimensionsConstants'

import trackingUtil from 'utils/TrackingUtil'
import UrlUtil from 'utils/UrlUtil'
import TranscriptDropdown from './transcript_nav/TranscriptDropdown';

import {
  TRANSCRIPT_TITLE,
  DEMOGRAPHICS_TITLE,
  CODED_BY_ME_TITLE,
  COMPARISON_TITLE,
} from 'constants/TranscriptNavigationConstants'

import DemographicsView from './DemographicsView'
import TranscriptBodyContainer from './TranscriptBodyContainer'
import TranscriptCodePageContainer from './TranscriptCodePageContainer'
import ComparisonViewContainer from './ComparisonViewContainer'

const tabStyle = {
    padding: `20px 20px 0px ${TRANSCRIPT_NAVIGATION_PADDING}px`,
    margin: "0px"
}

const TranscriptNav = (props) => {
  const history = useHistory();
  const location = useLocation();
  const tabDiv = useRef(null);
  const queryClient = useQueryClient();
  
  // Check for 404 error
  const { NotFoundComponent } = useTranscriptWithErrorHandling(props.transcriptID);
  
  // Get transcript data from react-query
  const { data: transcript, isSuccess } = useTranscript(props.transcriptID);
  
  // Get transcript body for page count calculation
  const transcriptBody = transcript?.body || null;
  const pageCount = useNumberOfPages(transcriptBody);
  
  const [height, setHeight] = useState(300);
  const [showInterCoderReliability, setShowInterCoderReliability] = useState(false);

  const handleWindowResize = useCallback(() => {
    let newHeight = 300;
    if (tabDiv.current) {
      newHeight = window.innerHeight - tabDiv.current.clientHeight - 50;
    }
    setHeight(newHeight);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    handleWindowResize();

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [handleWindowResize]);

  // The transcript loaded state is now derived from react-query
  const transcriptLoaded = isSuccess && !!transcript;

  const setInterCoderReliabilityWithLogging = useCallback((show) => {
    if (!!show) {
      LogAPI.logEvent('view-inter-coder-reliability');
    }
    setShowInterCoderReliability(show);
  }, []);

  const gotoUrl = useCallback((url) => {
    history.push(url);
  }, [history]);

  const gotoTranscript = useCallback((queryParams) => {
    history.push(`${location.pathname}${queryParams}`);
  }, [history, location.pathname]);

  const onNavigationClick = useCallback((transcriptView) => {
    const page = UrlUtil.getPageNumber(location);
    
    let url = transcriptView ? 
      `/transcripts/${props.transcriptID}/${transcriptView}` : 
      `/transcripts/${props.transcriptID}`;

    if (!!page && page > 1) {
      url = `${url}?page=${page}`;
    }

    // Invalidate code list when navigating
    queryClient.invalidateQueries({ queryKey: codeKeys.list(props.projectID) });

    gotoUrl(url);
  }, [props.transcriptID, props.projectID, location, gotoUrl, queryClient]);

  const gotoCompare = useCallback((url) => {
    gotoUrl(url);
    trackingUtil.logEvent('navigate-to-coding-comparison');
  }, [gotoUrl]);

  const gotoCodedByMe = useCallback(() => {
    onNavigationClick('user/me');
    trackingUtil.logEvent('navigate-to-coded-by-me');
  }, [onNavigationClick]);

  const gotoDemographics = useCallback(() => {
    const url = `/transcripts/${props.transcriptID}/demographics`;
    gotoUrl(url);
  }, [props.transcriptID, gotoUrl]);

  const getNavigationTitle = useCallback(() => {
    const path = location.pathname;
    if (path.includes('/user/me')) return CODED_BY_ME_TITLE;
    if (path.includes('/demographics')) return DEMOGRAPHICS_TITLE;
    if (path.includes('/compare')) return COMPARISON_TITLE;
    return TRANSCRIPT_TITLE;
  }, [location.pathname]);

  const renderWithPageInfo = useCallback((Component, otherProps) => {
    return (routeProps) => {
      return (
        <Component
          projectID={props.projectID}
          transcriptID={props.transcriptID}
          hasEditPermission={props.hasEditPermission}
          pageCount={pageCount}
          {...routeProps}
          {...(otherProps || {})}
        />
      );
    };
  }, [props.projectID, props.transcriptID, props.hasEditPermission, pageCount]);

  // Handle 404 error
  if (NotFoundComponent) {
    return <NotFoundComponent />;
  }

  const page = UrlUtil.getPageNumber(location);

  return (
    <div style={{height: "100%"}}>
      <MemoModalProvider>
        <TranscriptCodePageContainer
          projectID={props.projectID}
          hasEditPermission={props.hasEditPermission}
        />
      </MemoModalProvider>
      <div style={tabStyle} ref={tabDiv}>
        <TranscriptDropdown
          title={getNavigationTitle()}
          onNavigationClick={onNavigationClick}
          gotoDemographics={gotoDemographics}
          gotoCompare={gotoCompare}
          gotoCodedByMe={gotoCodedByMe}
          projectID={props.projectID}
          transcriptID={props.transcriptID}
          page={page}
        />

        <Switch>
          <Route
            path='/transcripts/:transcriptID/compare'
            render={() => {
              return (
                <Button
                  disabled={!transcriptLoaded}
                  variant="link"
                  onClick={() => setInterCoderReliabilityWithLogging(true)}
                >
                  Inter Coder Reliability
                </Button>
              );
            }}
          />
        </Switch>
      </div>

      <div style={{height: height.toString() + "px"}}>
        <Switch>
          <Route
            exact
            path='/transcripts/:transcriptID'
            render={renderWithPageInfo(TranscriptBodyContainer)}
          />
          <Route
            path='/transcripts/:transcriptID/demographics'
            render={renderWithPageInfo(DemographicsView)}
          />
          <Route
            path='/transcripts/:transcriptID/compare'
            render={renderWithPageInfo(ComparisonViewContainer, {
              showInterCoderReliability: showInterCoderReliability,
              onHideInterCoderReliability: () => setInterCoderReliabilityWithLogging(false),
            })}
          />
          <Route
            exact
            path='/transcripts/:transcriptID/user/:userID'
            render={renderWithPageInfo(TranscriptBodyContainer, {userID: "TODO_USER_ID"})}
          />
        </Switch>
      </div>
    </div>
  );
};

export default TranscriptNav;